/**
 * Public CMS data fetching for the public website (no auth).
 * Uses simple in-memory cache so the same page/settings request is shared
 * across components mounted in the same render tree.
 */

import { useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PUBLIC_API = `${BACKEND_URL}/api/public`;

export const cmsMediaUrl = (mediaOrPath) => {
  if (!mediaOrPath) return null;
  if (typeof mediaOrPath === "string") {
    if (mediaOrPath.startsWith("http")) return mediaOrPath;
    return `${BACKEND_URL}${mediaOrPath}`;
  }
  if (mediaOrPath.url) {
    return mediaOrPath.url.startsWith("http")
      ? mediaOrPath.url
      : `${BACKEND_URL}${mediaOrPath.url}`;
  }
  return null;
};

const cache = new Map();

const getCached = async (key, fn) => {
  if (cache.has(key)) return cache.get(key);
  const promise = fn().then(
    (val) => {
      cache.set(key, Promise.resolve(val));
      return val;
    },
    (err) => {
      cache.delete(key);
      throw err;
    },
  );
  cache.set(key, promise);
  return promise;
};

export const invalidateCmsCache = () => cache.clear();

export const fetchSettings = () =>
  getCached("settings", async () => {
    const { data } = await axios.get(`${PUBLIC_API}/settings`);
    return data;
  });

export const fetchPage = (slugOrKey) =>
  getCached(`page:${slugOrKey}`, async () => {
    const { data } = await axios.get(`${PUBLIC_API}/pages/${slugOrKey}`);
    return data;
  });

export const fetchStories = () =>
  getCached("stories", async () => {
    const { data } = await axios.get(`${PUBLIC_API}/stories`);
    return data;
  });

export const fetchStory = (slug) =>
  getCached(`story:${slug}`, async () => {
    const { data } = await axios.get(`${PUBLIC_API}/stories/${slug}`);
    return data;
  });

// React hooks
export const useSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    fetchSettings()
      .then((d) => !cancelled && setSettings(d))
      .catch(() => !cancelled && setSettings({}))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);
  return { settings, loading };
};

export const usePage = (slugOrKey) => {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchPage(slugOrKey)
      .then((d) => !cancelled && setPage(d))
      .catch((e) => !cancelled && setError(e))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [slugOrKey]);
  return { page, loading, error };
};

export const useStories = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    fetchStories()
      .then((d) => !cancelled && setStories(d || []))
      .catch(() => !cancelled && setStories([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);
  return { stories, loading };
};

export const useStory = (slug) => {
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchStory(slug)
      .then((d) => !cancelled && setStory(d))
      .catch((e) => !cancelled && setError(e))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [slug]);
  return { story, loading, error };
};

// Set the document title from CMS SEO fields
export const useSeo = ({ title, description }) => {
  useEffect(() => {
    if (title) document.title = title;
    if (description) {
      let meta = document.querySelector("meta[name='description']");
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute("name", "description");
        document.head.appendChild(meta);
      }
      meta.setAttribute("content", description);
    }
  }, [title, description]);
};
